import { useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  Input,
  PrimaryButton,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { DsSelect } from '~/components/common/DsSelect'
import { FormSelect } from '~/components/common/FormSelect'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { CharCount } from '~/pages/AdminPlantUnitMaster/components/CharCount'
import {
  GENERATION_SITE_NAME_MAX_LENGTH,
  buildGenerationSiteFormSchema,
} from '~/pages/AdminPlantUnitMaster/models/siteForm'
import { siteCandidates } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  PENDING_SITE_ID,
  type PlantUnitFormInput,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'

/** 登録ダイアログでその場で「発電所を追加」した、まだ保存していない発電所。ユニットの保存と同時に作る。 */
export type PendingSite = { siteName: string; areaCode: AreaCode }

type SiteOption = { id: string; siteName: string; areaCode: AreaCode }

type Props = {
  control: Control<PlantUnitFormInput>
  inputId: string
  sites: readonly SiteOption[]
  /** 候補を絞るエリア (フォームのエリア欄)。UNSPECIFIED = 絞らない。 */
  areaCode: AreaCode
  /** 今選ばれている発電所。 */
  currentSiteId: string
  /** 選べない (エリア未選択の間)。「発電所を追加」も押せない。 */
  isDisabled?: boolean
  /** 発電所を選び直した時 (フォームへの反映後)。値は発電所 id、クリア時は undefined。 */
  onSiteChanged?: (siteId: string | undefined) => void
  /** その場で「発電所を追加」できるか (登録ダイアログだけ。詳細ページは「発電所」タブで足す)。 */
  allowAdd?: boolean
  /** まだ保存していない追加分 (候補に仮 id で出す)。 */
  pendingSite?: PendingSite | undefined
  /** その場で追加した発電所を選ぶ (フォームの値を仮 id に書き換える。保存はユニットと同時)。 */
  onSiteAdded?: (site: PendingSite) => void
}

/**
 * 電源ユニットの「発電所」欄 = 発電所 (サイト) の選択 + その場で「発電所を追加」。
 *
 * 追加は名称とエリアだけを入れる。ここでは作らず、ユニットの保存が通った時だけ一緒に作られる
 * (キャンセルや検証エラーなら作られない)。
 */
export const SiteSelectField = ({
  control,
  inputId,
  sites,
  areaCode,
  currentSiteId,
  allowAdd = false,
  isDisabled = false,
  onSiteChanged,
  pendingSite,
  onSiteAdded,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [isAdding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [newAreaCode, setNewAreaCode] = useState(areaCode)
  const [addError, setAddError] = useState<string | undefined>(undefined)

  const options = [
    ...(pendingSite === undefined
      ? []
      : [{ value: PENDING_SITE_ID, label: pendingSite.siteName }]),
    ...siteCandidates(sites, { areaCode, currentSiteId }).map((site) => ({
      value: site.id,
      label: site.siteName,
    })),
  ]

  const openAdd = (): void => {
    setNewName('')
    setNewAreaCode(areaCode)
    setAddError(undefined)
    setAdding(true)
  }

  const submitAdd = (): void => {
    const parsed = buildGenerationSiteFormSchema(t)
      .pick({ siteName: true, areaCode: true })
      .safeParse({ siteName: newName, areaCode: newAreaCode })
    if (!parsed.success) {
      setAddError(parsed.error.issues[0]?.message)
      return
    }
    setAdding(false)
    onSiteAdded?.({
      siteName: parsed.data.siteName,
      areaCode: parsed.data.areaCode,
    })
  }

  return (
    <Flex flexDirection="column" gap={2} width="100%">
      <Flex alignItems="center" gap={2}>
        <Box flex="1" minWidth={0}>
          <FormSelect
            aria-label={t('page:adminPlantUnitMaster.unit.field.site')}
            control={control}
            inputId={inputId}
            isDisabled={isDisabled}
            name="siteId"
            onValueChange={onSiteChanged}
            options={options}
            placeholder={t(
              'page:adminPlantUnitMaster.unit.field.sitePlaceholder',
            )}
          />
        </Box>
        {allowAdd && !isAdding && (
          <TertiaryButton
            flexShrink={0}
            disabled={isDisabled}
            onClick={openAdd}
            size="sm"
          >
            {t('page:adminPlantUnitMaster.siteAdd.open')}
          </TertiaryButton>
        )}
      </Flex>
      {isAdding && (
        <Flex
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          padding={2}
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="md"
        >
          <Text as="span" textStyle="caption" color="Text.midEmphasis">
            {t('page:adminPlantUnitMaster.siteAdd.name')}
          </Text>
          <Input
            width="220px"
            aria-label={t('page:adminPlantUnitMaster.siteAdd.name')}
            disabled={isDisabled}
            onChange={(event) => setNewName(event.target.value)}
            size="sm"
            value={newName}
          />
          <CharCount max={GENERATION_SITE_NAME_MAX_LENGTH} value={newName} />
          <Text as="span" textStyle="caption" color="Text.midEmphasis">
            {t('page:adminPlantUnitMaster.siteAdd.area')}
          </Text>
          <DsSelect
            width="140px"
            aria-label={t('page:adminPlantUnitMaster.siteAdd.area')}
            isDisabled={isDisabled}
            onChange={(value) =>
              setNewAreaCode(value === undefined ? areaCode : Number(value))
            }
            options={AREA_CODE_OPTIONS.map((code) => ({
              value: String(code),
              label: convertAreaLabel(code),
            }))}
            value={String(newAreaCode)}
          />
          <PrimaryButton disabled={isDisabled} onClick={submitAdd} size="sm">
            {t('page:adminPlantUnitMaster.siteAdd.confirm')}
          </PrimaryButton>
          <TertiaryButton
            disabled={isDisabled}
            onClick={() => setAdding(false)}
            size="sm"
          >
            {t('common:action.cancel')}
          </TertiaryButton>
          {addError !== undefined && (
            <Text as="span" textStyle="caption" color="Text.negativeHigh">
              {addError}
            </Text>
          )}
        </Flex>
      )}
    </Flex>
  )
}
